import { CircleCheckIcon, InfoIcon, Loader2Icon, TriangleAlertIcon, XCircleIcon, XIcon } from '@myosh/icons';
import { type ComponentType, type CSSProperties, useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { cn } from '../../common/functions/cn';
import { Button } from '../button/button';
import { dismiss, removeToast, type ToastRecord, type ToastType } from './toast-manager';

const TOAST_ICONS: Record<ToastType, ComponentType<{ className?: string }>> = {
  info: InfoIcon,
  loading: Loader2Icon,
  success: CircleCheckIcon,
  warning: TriangleAlertIcon,
  error: XCircleIcon,
};

interface ToastProps {
  toast: ToastRecord;
  onReleaseFocus: () => void;
}

const Toast = ({ toast, onReleaseFocus }: ToastProps) => {
  const { t } = useTranslation();
  const { id, closing, type, title, description, duration, action, dismissible, shown } = toast;
  const Icon = type ? TOAST_ICONS[type] : undefined;
  const ref = useRef<HTMLDivElement>(null);
  const clock = useRef(shown);

  useLayoutEffect(() => {
    if (clock.current === shown) {
      return;
    }

    clock.current = shown;

    for (const animation of ref.current?.getAnimations?.() ?? []) {
      animation.currentTime = 0;
    }
  }, [shown]);

  /* Handed on as the exit starts, while the toast is still there to be asked. Focus falling to
     `body` on removal would put a keyboard user back at the top of the page. */
  useLayoutEffect(() => {
    if (closing && ref.current?.contains(document.activeElement)) {
      onReleaseFocus();
    }
  }, [closing, onReleaseFocus]);

  return (
    <div
      ref={ref}
      className="flex items-center gap-2.5 rounded-md border border-border bg-popover p-3 text-popover-foreground text-sm shadow-md focus-visible:outline-2 focus-visible:outline-ring focus-visible:-outline-offset-2"
      data-slot="toast"
      tabIndex={-1}
      data-state={closing ? 'closed' : undefined}
      data-sticky={duration <= 0 ? '' : undefined}
      style={{ '--toast-duration': `${duration}ms` } as CSSProperties}
      onAnimationEnd={(event) => {
        if (event.target !== event.currentTarget) {
          return;
        }

        if (event.animationName === 'toast-timeout') {
          dismiss(id);
        }

        if (event.animationName === 'toast-exit') {
          removeToast(id);
        }
      }}
    >
      {Icon && (
        <Icon
          className={cn(
            'size-4 shrink-0',
            type === 'error' && 'text-error',
            type === 'success' && 'text-success',
            type === 'warning' && 'text-warning',
            type === 'loading' && 'animate-spin'
          )}
        />
      )}
      <span className="wrap-break-word flex min-h-7 min-w-0 flex-1 flex-col justify-center gap-0.5">
        <span data-slot="toast-title">{title}</span>
        {description && (
          <span data-slot="toast-description" className="text-muted-foreground">
            {description}
          </span>
        )}
      </span>
      {action && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            action.onClick?.();
            dismiss(id);
          }}
        >
          {action.label}
        </Button>
      )}
      {dismissible && (
        <Button
          variant="ghost"
          size="icon-sm"
          className="relative text-muted-foreground after:absolute after:-inset-2"
          onClick={() => dismiss(id)}
        >
          <XIcon />
          <span className="sr-only">{t(($) => $.close)}</span>
        </Button>
      )}
    </div>
  );
};

export { Toast, type ToastProps };
